import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputAdornment,
  InputLabel,
  ListItemText,
  MenuItem,
  TextField,
} from '@mui/material';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import {
  createPaymentNotification,
  getInvoicesByEncryptionBusinessIdentifier,
} from 'lib/api/paymentLink';
import { Invoice } from 'lib/api/paymentLink/types';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import { formatMoney, formatMoneyBack, showAlert } from 'lib/functions/utils';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

interface NotifyPaymentProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  invoice: Invoice;
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const NotifyPayment: React.FC<NotifyPaymentProps> = (props) => {
  const { open, setOpen, invoice } = props;
  const { encryptedCompanyId, encryptedBusinessIdentifier } = useParams<{
    encryptedCompanyId: string;
    encryptedBusinessIdentifier: string;
  }>();
  const dispatch = useDispatch();
  const { data, isLoading } = getInvoicesByEncryptionBusinessIdentifier(
    encryptedCompanyId,
    encryptedBusinessIdentifier,
  );
  const { mutate: createPaymentNotificationMutation } =
    createPaymentNotification(encryptedCompanyId, encryptedBusinessIdentifier);
  const invoices = data?.invoices ?? [];
  const [amount, setAmount] = useState<number>(invoice.amount);
  const [message, setMessage] = useState('');
  const [checkFields, setCheckField] = useState(false);
  const [invoicesIds, setInvoicesIds] = useState<string[]>([invoice.id]);
  const [sending, setSending] = useState(false);

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const {
      target: { value },
    } = event;
    setInvoicesIds(typeof value === 'string' ? value.split(',') : value);
  };

  const postPaymentNotification = async () => {
    setCheckField(true);
    if (invoicesIds.length === 0) {
      return;
    }
    setSending(true);
    void createPaymentNotificationMutation(
      {
        amount: formatMoneyBack(amount.toString()),
        message: message,
        invoices: invoicesIds,
      },
      {
        onSuccess: () => {
          setAmount(0);
          setMessage('');
          setInvoicesIds([]);
          setCheckField(false);
          setSending(false);
          showAlert(
            dispatch,
            'success',
            'La notificación de pago ha sido enviada correctamente',
          );
          setOpen(false);
        },
        onError: () => {
          setSending(false);
          showAlert(
            dispatch,
            'error',
            'Ha habido un error al enviar la notificación de pago',
          );
        },
      },
    );
  };

  return (
    <Dialog open={open} onClose={() => setOpen(false)}>
      {(isLoading || sending) && <CenteredCircularProgress type="dialog" />}
      {!isLoading && (
        <>
          <DialogTitle> 📢 Informar nuevo pago</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Para informar un nuevo pago debes completar los siguientes campos
            </DialogContentText>
            <div style={{ paddingTop: '2%' }}>
              <TextField
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
                error={checkFields && !amount}
                onChange={(e) => {
                  const value = formatMoneyBack(e.target.value);
                  setAmount(typeof value === 'number' ? value : Number(value));
                }}
                id="outlined-basic"
                label="Monto pagado"
                variant="outlined"
                value={formatMoney(amount).replace('$', '')}
                fullWidth
              />
            </div>
            <div style={{ paddingTop: '2%' }}>
              <FormControl fullWidth>
                <InputLabel id="select-invoices">Documentos</InputLabel>
                <Select
                  id="demo-multiple-checkbox"
                  error={checkFields && invoicesIds.length === 0}
                  multiple
                  fullWidth
                  value={invoicesIds}
                  label="Docuementos"
                  onChange={handleChange}
                  MenuProps={MenuProps}
                  labelId="select-invoices"
                  renderValue={(selected) =>
                    selected
                      .map(
                        (value) =>
                          `#${invoices.find((invoice) => invoice.id === value)?.folio}`,
                      )
                      .join(', ')
                  }
                >
                  {invoices.map((invoice) => (
                    <MenuItem key={invoice.folio} value={invoice.id}>
                      <Checkbox checked={invoicesIds.includes(invoice.id)} />
                      <ListItemText primary={`# ${invoice.folio}`} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <div style={{ paddingTop: '2%' }}>
              <TextField
                id="outlined-basic"
                label="Mensaje"
                variant="outlined"
                fullWidth
                multiline
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            </div>
          </DialogContent>
          <DialogActions>
            <CustomButton color="secondary" onClick={() => setOpen(false)}>
              {' '}
              Cancelar{' '}
            </CustomButton>
            <CustomButton
              disabled={sending || isLoading}
              color="primary"
              onClick={() => {
                void postPaymentNotification();
              }}
            >
              {' '}
              Notificar
            </CustomButton>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default NotifyPayment;
